import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Alert,
  Chip
} from '@mui/material';
import { CloudUpload, InsertDriveFile, Settings } from '@mui/icons-material';
import { uploadFile } from './api/fileApi';

const UploadTTLTab = ({
  selectedConfigId,
  translationConfigs = [],
  setSelectedConfigId,
  setShowConfigManager,
  onUploadSuccess,
  showSuccess,
  showError,
  userPermissions
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Seleccionar automáticamente la configuración marcada como default
  useEffect(() => {
    if (!selectedConfigId && translationConfigs.length > 0) {
      const defaultConfig = translationConfigs.find(config => config.is_default);
      if (defaultConfig) {
        console.log('[Upload] 🎯 Auto-selected default translation config:', {
          id: defaultConfig.id,
          name: defaultConfig.name,
          target_language: defaultConfig.target_language,
          glossaries: defaultConfig.glossaries?.length ?? 0
        });
        setSelectedConfigId(defaultConfig.id);
      } else {
        console.log('[Upload] ⚠️ No default translation config found among', translationConfigs.length, 'configs');
      }
    }
  }, [translationConfigs, selectedConfigId, setSelectedConfigId]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('[Upload] 📄 File selected:', {
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        type: file.type || '(no mime type)',
        lastModified: new Date(file.lastModified).toISOString()
      });
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    const selectedConfig = translationConfigs.find(c => c.id === selectedConfigId);

    console.group('[Upload] 🚀 Starting file upload');
    console.log('File:', selectedFile.name, `(${(selectedFile.size / 1024).toFixed(1)} KB)`);
    console.log('Translation config:', selectedConfigId
      ? `${selectedConfig?.name} → ${selectedConfig?.target_language} (id=${selectedConfigId})`
      : 'None (no config selected)');
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();

    setUploading(true);
    const uploadStart = performance.now();

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Añadir configuración de traducción si está seleccionada
      if (selectedConfigId) {
        formData.append('translation_config_id', selectedConfigId);
      }

      const onUploadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (pct % 25 === 0) {
            console.log(`[Upload] 📡 HTTP transfer: ${pct}% — ${(progressEvent.loaded / 1024).toFixed(1)} / ${(progressEvent.total / 1024).toFixed(1)} KB`);
          }
        }
      };

      console.log('[Upload] 📤 Sending POST to files/upload/ …');
      const result = await uploadFile(formData, onUploadProgress);
      const elapsed = ((performance.now() - uploadStart) / 1000).toFixed(2);

      console.group('[Upload] ✅ Upload accepted by server');
      console.log('File ID:', result?.id);
      console.log('Name:', result?.name);
      console.log('Size (server):', result?.file_size);
      console.log('Message:', result?.message);
      console.log('Elapsed:', `${elapsed}s`);
      console.log('ℹ️  Backend will now run TTLProcessor → create TTLRequirement records → dispatch Celery translation task');
      console.groupEnd();

      setSelectedFile(null);
      
      // Show success message
      if (showSuccess) {
        showSuccess('File uploaded successfully!');
      }
      
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      const elapsed = ((performance.now() - uploadStart) / 1000).toFixed(2);
      console.group('[Upload] ❌ Upload failed');
      console.error('Error:', error.message);
      console.error('HTTP status:', error.response?.status);
      console.error('Server response:', error.response?.data);
      console.error('Elapsed:', `${elapsed}s`);
      console.groupEnd();

      // Show error message
      if (showError) {
        let errorMessage = error.response?.data?.error || error.message || 'Error uploading file';
        const errorDetails = error.response?.data?.details || '';
        
        // Detectar error de columnas faltantes en Excel
        if (errorMessage.includes('Missing required columns') || 
            errorDetails.includes('Missing required columns') ||
            errorMessage.includes('Invalid Excel structure')) {
          errorMessage = 'Invalid Excel format: The file is missing required columns (phase, label, notation, requirement_text, responsible, version). Please use the correct template.';
        }
        
        showError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start',
      pt: 4,
      pb: 4
    }}>
      <Card 
        elevation={12} 
        sx={{ 
          maxWidth: 600, 
          width: '100%',
          borderRadius: 3 
        }}
      >
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#616161', color: '#ffffff' }}>
              <CloudUpload />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121' }}>
              Upload TTL File
            </Typography>
          }
          subheader="Turtle/TTL or Excel (.xlsx) format supported"
        />
        <Divider sx={{ borderColor: '#e0e0e0' }} />
        <CardContent>
          <Box component="form" onSubmit={handleFileUpload}>
            {/* Selector de configuración de traducción */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Translation Configuration
                </Typography>
                {userPermissions?.can_create && (
                  <IconButton 
                    size="small" 
                    onClick={() => setShowConfigManager(true)}
                    title="Manage configurations"
                  >
                    <Settings fontSize="small" />
                  </IconButton>
                )}
              </Box>
              
              {translationConfigs.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {userPermissions?.can_create 
                    ? 'No translation configurations found. Click the gear icon to create one.'
                    : 'No translation configurations available. Contact an administrator to create configurations.'}
                </Alert>
              )}
              
              <FormControl fullWidth size="small">
                <InputLabel>Select configuration</InputLabel>
                <Select
                  value={selectedConfigId || ''}
                  onChange={(e) => setSelectedConfigId(e.target.value)}
                  label="Select configuration"
                >
                  <MenuItem value="">
                    <em>No configuration (default)</em>
                  </MenuItem>
                  {Array.isArray(translationConfigs) && translationConfigs.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.name} → {config.target_language}
                      {config.is_default && ' (default)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedConfigId && Array.isArray(translationConfigs) && (() => {
                const selectedConfig = translationConfigs.find(c => c.id === selectedConfigId);
                return selectedConfig ? (
                  <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      <strong>Domain:</strong> {selectedConfig.domain || 'Not specified'}
                    </Typography>
                    {selectedConfig.context && (
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        <strong>Context:</strong> {selectedConfig.context.substring(0, 100)}...
                      </Typography>
                    )}
                    {selectedConfig.glossaries && selectedConfig.glossaries.length > 0 && (
                      <Box mt={1}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          <strong>Active Glossaries:</strong>
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {selectedConfig.glossaries.map(glossary => (
                            <Chip 
                              key={glossary.id}
                              label={glossary.name} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ) : null;
              })()}
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <input
              type="file"
              accept=".ttl,.turtle,.xlsx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-input"
            />
            <label htmlFor="file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<InsertDriveFile />}
                sx={{ 
                  mb: 3, 
                  py: 2,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderColor: 'primary.main',
                    bgcolor: 'grey.50'
                  }
                }}
              >
                {selectedFile ? selectedFile.name : 'Select TTL or Excel File'}
              </Button>
            </label>
            
            {selectedFile && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="body2" color="primary.main">
                  <strong>Selected:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Size:</strong> {formatFileSize(selectedFile.size)}
                </Typography>
              </Paper>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={uploading || !selectedFile}
              startIcon={<CloudUpload />}
              sx={{ 
                py: 1.5,
                background: uploading ? '#9e9e9e' : '#000000',
                boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
                color: '#ffffff',
                '&:hover': {
                  background: uploading ? '#9e9e9e' : '#212121'
                }
              }}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
            {uploading && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UploadTTLTab;
